import React, { useMemo } from 'react';
import {
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { Text } from '@/components/common/text';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { GlassCard } from '@/components/shared/redesign/GlassCard';
import { OccurrenceItemRow } from '@/components/admin/todoLists/OccurrenceItemRow';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import type {
  AvailableAssignee,
  ItemStatus,
  TodoList,
} from '@/types/admin/todoLists';
import { CADENCE_LABEL } from '../mobile/ToDosList';
import {
  ARCHIVED_READ_ONLY_LABEL,
  ERROR_LABEL,
  NO_PERIOD_LABEL,
  SavedBanner,
  buildAssigneeNameMap,
  sortVisibleItems,
} from '../mobile/TodoListOccurrenceView';
import type { SavedBannerVariant } from '../mobile/TodoListOccurrenceView';

interface Props {
  list: TodoList;
  assignees: AvailableAssignee[];
  isLoading: boolean;
  isError: boolean;
  onSetItemStatus: (itemId: number, status: ItemStatus) => void;
  /** Back affordance handler — returns to the To Do's index. */
  onBack: () => void;
  /** When set, renders an Edit affordance that opens the edit form. */
  onEditList?: () => void;
  /** Transient post-save confirmation shown above the checklist; null hides it. */
  savedBanner?: SavedBannerVariant | null;
}

/**
 * Desktop occurrence detail. DesktopAdminLayout is a fixed windowHeight box on
 * web, so the content is wrapped in a vertical ScrollView (mirrors
 * DesktopToDosView).
 */
export const DesktopTodoListOccurrenceView: React.FC<Props> = ({
  list,
  assignees,
  isError,
  onSetItemStatus,
  onBack,
  onEditList,
  savedBanner,
}) => {
  const { redesign } = useAdminColors();
  const nameMap = useMemo(() => buildAssigneeNameMap(assignees), [assignees]);
  const occurrence = list.currentOccurrence;
  const items = useMemo(
    () => (occurrence ? sortVisibleItems(occurrence.items) : []),
    [occurrence],
  );
  const completed = occurrence?.status === 'completed';

  const subtitle = `${CADENCE_LABEL[list.cadence]}${
    occurrence ? ` · ${occurrence.periodDate}` : ''
  }`;

  return (
    <DesktopAdminLayout>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator
      >
        <TouchableOpacity
          testID="todo-list-back-button"
          onPress={onBack}
          accessibilityRole="button"
          accessibilityLabel="Back to To Do's"
          style={styles.backLink}
        >
          <Text
            style={[styles.backLinkText, { color: redesign.text.secondary }]}
          >
            ‹ To Do's
          </Text>
        </TouchableOpacity>

        {/*
          The Edit action rides DesktopPageHeader's primary add-button slot so it
          inherits the exact filled/accent header-action treatment the index's
          "New to-do list" uses (far right, aligned with the title block). The
          completed pill and the archived read-only label share the same
          right-aligned actions slot, keeping the header geometry stable across
          active / completed / archived states.
        */}
        <DesktopPageHeader
          title={list.title}
          subtitle={subtitle}
          showAddButton={onEditList != null}
          onAddPress={onEditList}
          addButtonText="Edit list"
          addButtonTestID="todo-list-edit-button"
          addButtonAccessibilityLabel="Edit list"
        >
          {completed && (
            <View
              style={[
                styles.completedPill,
                { backgroundColor: redesign.accent.greenBg },
              ]}
            >
              <Text
                style={[styles.completedText, { color: redesign.accent.green }]}
              >
                Completed
              </Text>
            </View>
          )}
          {onEditList == null && list.status === 'archived' ? (
            <Text style={[styles.archivedText, { color: redesign.text.muted }]}>
              {ARCHIVED_READ_ONLY_LABEL}
            </Text>
          ) : null}
        </DesktopPageHeader>

        <SavedBanner variant={savedBanner} />

        {isError ? (
          <Text style={[styles.stateText, { color: redesign.text.muted }]}>
            {ERROR_LABEL}
          </Text>
        ) : !occurrence ? (
          <Text style={[styles.stateText, { color: redesign.text.muted }]}>
            {NO_PERIOD_LABEL}
          </Text>
        ) : (
          <GlassCard variant="soft" style={styles.card}>
            {items.map((item, index) => (
              <View key={item.id}>
                {index > 0 && (
                  <View
                    style={[
                      styles.divider,
                      { backgroundColor: redesign.separator },
                    ]}
                  />
                )}
                <OccurrenceItemRow
                  item={item}
                  completedByName={
                    item.completedByAccountId != null
                      ? nameMap.get(item.completedByAccountId)
                      : undefined
                  }
                  onChange={status => onSetItemStatus(item.id, status)}
                />
              </View>
            ))}
          </GlassCard>
        )}
      </ScrollView>
    </DesktopAdminLayout>
  );
};

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  scrollContent: { paddingBottom: 24, gap: 16 },
  backLink: { alignSelf: 'flex-start', paddingVertical: 4 },
  // Font size intentionally left at the platform default (14 — the same size
  // the survey back-link sets explicitly) to avoid a new inline typographic
  // literal; color uses the redesign namespace rather than the survey screen's
  // residents link token.
  backLinkText: { fontWeight: '600' },
  completedPill: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
  },
  completedText: { fontSize: 11, fontWeight: '700' },
  // Read-only marker shown in the header's right-aligned action slot for an
  // archived list (where the Edit action would otherwise sit).
  archivedText: { fontSize: 14, fontWeight: '600' },
  stateText: { fontSize: 15, textAlign: 'center', paddingVertical: 40 },
  card: { paddingVertical: 4 },
  divider: { height: StyleSheet.hairlineWidth },
});
